import React from 'react';
import PropTypes from 'prop-types';
import { convertColorToThemeHexCode } from 'Styles/colors.util';
import * as Styled from './Block.styled';
import CTAPropTypes from '../../prop-types/cta';
import CTABlock from '../CTABlock/CTABlock';
import { CustomPropType } from './Block.utils';

/**
 * `Block` displays pre-styled HTML in between an optional icon and button(s).
 */
const Block = ({
  textAlignment,
  backgroundColor,
  ctas,
  icon,
  paddingTop,
  paddingRight,
  paddingBottom,
  paddingLeft,
  className,
  children
}) => (
  <Styled.Block
    $backgroundColor={backgroundColor}
    paddingTop={paddingTop}
    paddingRight={paddingRight}
    paddingBottom={paddingBottom}
    paddingLeft={paddingLeft}
    className={className}
  >
    <Styled.Editorial $alignment={textAlignment}>
      {icon?.file?.url && (
        <Styled.Icon
          $alignment={textAlignment}
          src={icon.file.url}
          alt="icon"
        />
      )}
      {children && (
        <Styled.RichText $backgroundColor={backgroundColor}>
          {children}
        </Styled.RichText>
      )}
      {!!ctas?.length && <CTABlock ctas={ctas} color={backgroundColor} />}
    </Styled.Editorial>
  </Styled.Block>
);

Block.propTypes = {
  /**
   * Left align or center variation
   */
  textAlignment: PropTypes.oneOf(['LEFT', 'CENTER']),
  /**
   * A hex value to use as the background color of `Block`
   */
  backgroundColor: PropTypes.string,
  /**
   * Content to display in block. Only raw HTML accepted.
   */
  children: CustomPropType.anyOfDeepTags(
    ['h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'p', 'li', 'a', 'b', 'i'],
    '__skipValidate'
  ),
  /**
   * For internal use only.
   * Used to bypass `children` validation due to complexities with validation.
   */
  __skipValidate: PropTypes.bool, // eslint-disable-line
  /**
   * CSS Classname
   */
  className: PropTypes.string,
  /**
   * A list of links
   */
  ctas: PropTypes.arrayOf(PropTypes.shape(CTAPropTypes)),
  /**
   * An icon object
   */
  icon: PropTypes.shape({
    file: PropTypes.shape({
      /**
       * An image URL
       */
      url: PropTypes.string.isRequired
    })
  }),
  /**
   * overrides top padding if necessary
   * if not passed, uses default padding set in the styles
   */
  paddingTop: PropTypes.string,
  /**
   * overrides right padding if necessary
   * if not passed, uses default padding set in the styles
   */
  paddingRight: PropTypes.string,
  /**
   * overrides bottom padding if necessary
   * if not passed, uses default padding set in the styles
   */
  paddingBottom: PropTypes.string,
  /**
   * overrides left padding if necessary
   * if not passed, uses default padding set in the styles
   */
  paddingLeft: PropTypes.string
};

Block.defaultProps = {
  textAlignment: 'CENTER',
  backgroundColor: convertColorToThemeHexCode('white'),
  children: null,
  className: '',
  ctas: [],
  icon: null,
  paddingTop: '',
  paddingRight: '',
  paddingBottom: '',
  paddingLeft: ''
};

export default Block;
